import React, { useCallback, useEffect, useState } from 'react';
import {
    EuiCard,
    EuiCollapsibleNavGroup,
    EuiFlexGroup,
    EuiFlexItem,
    EuiForm,
    EuiIcon,
    EuiLink,
    EuiSpacer,
    EuiText,
    EuiTitle,
} from '@elastic/eui';
import ButtonGroup from '../../../components/Form/ButtonGroup';
import NumberField from '../../../components/Form/NumberField';
import SwitchField from '../../../components/Form/SwitchField';
import CheckBoxField from '../../../components/Form/CheckBoxField';
import EarningsDelay from '../Components/Cashback/EarningsDelay';

const CashbackSettings = (props) => {
    const { restaurantResponse, enableTierSystem, control, errors, watch, setValue, getValues } = props;
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [cardDescription, setCardDescription] = useState([]);
    // Initialize state - will be populated from watched values
    const [tabOrderingModes, setTabOrderingModes] = useState([]);
    const [orderingModes, setOrderingModes] = useState([]);

    const updateFormValues = useCallback((data) => {
        setCardDescription([]);
        let defaultValue = {
            ordering_mode_cashback_configurations: [],
        };
        if (data?.loyalty_settings || data?.settings) {
            const settings = data.loyalty_settings || data.settings;
            defaultValue.cashback_earning_percentage = settings.cashback_earning_percentage;
            defaultValue.reward_point_expiration_duration = settings.reward_point_expiration_duration;
            defaultValue.signup_cashback = settings.signup_cashback;
            defaultValue.promo_consolidation = settings.promo_consolidation;
            const firstIndexCashbackPercentage = data?.ordering_mode_cashback_configurations?.find(
                (config) => config.cashback_redeem_percentage
            )?.cashback_redeem_percentage;
            const firstIndexCashbackDelay = data?.ordering_mode_cashback_configurations?.find(
                (config) => config.cashback_settlement_delay
            )?.cashback_settlement_delay;
            // const isSameConfig = data?.ordering_mode_cashback_configurations?.every(
            //     (config) => config?.cashback_redeem_percentage === firstIndexCashbackPercentage
            // );

            // defaultValue.is_same_config = isSameConfig;
            defaultValue.redeem_for_all_modes = firstIndexCashbackPercentage;

            const hours = firstIndexCashbackDelay ? parseInt(firstIndexCashbackDelay / 60) : 0;
            const mins = firstIndexCashbackDelay ? firstIndexCashbackDelay % 60 : 0;

            defaultValue.cashback_settlement_delay_in_hrs = hours;
            defaultValue.cashback_settlement_delay_in_mins = mins;

            let firstOrderingMode = [];

            for (const orderingMode of data?.ordering_modes) {
                if (data?.ordering_mode_cashback_configurations?.length) {
                    for (const configMode of data.ordering_mode_cashback_configurations) {
                        if (orderingMode.id === configMode.restaurant_ordering_mode_id && orderingMode.status) {
                            firstOrderingMode.push(orderingMode);
                            const hours = configMode?.cashback_settlement_delay
                                ? parseInt(configMode?.cashback_settlement_delay / 60)
                                : 0;
                            const mins = configMode?.cashback_settlement_delay
                                ? configMode?.cashback_settlement_delay % 60
                                : 0;
                            defaultValue.ordering_mode_cashback_configurations.push({
                                displayName: orderingMode.type,
                                restaurant_ordering_mode_id: orderingMode.id,
                                cashback_redeem_percentage: configMode?.cashback_redeem_percentage,
                                cashback_settlement_delay: configMode?.cashback_settlement_delay,
                                cashback_settlement_delay_in_hrs: hours,
                                cashback_settlement_delay_in_mins: mins,
                                ...orderingMode,
                            });
                        }
                    }
                } else {
                    if (orderingMode.status) {
                        defaultValue.ordering_mode_cashback_configurations.push({
                            displayName: orderingMode.type,
                            restaurant_ordering_mode_id: orderingMode.id,
                            cashback_redeem_percentage: null,
                            ...orderingMode,
                        });
                    }
                }
            }

            if (defaultValue?.ordering_mode_cashback_configurations?.length) {
                const returnThreeMainTabs = defaultValue?.ordering_mode_cashback_configurations?.filter(
                    (orderingMode) =>
                        orderingMode.tab === 'delivery' ||
                        orderingMode.tab === 'takeaway' ||
                        orderingMode.tab === 'dine_in'
                );

                const removeDuplicateTabs = returnThreeMainTabs?.reduce(
                    (acc, curr) => (acc.find((mode) => mode.tab === curr.tab) ? acc : [...acc, curr]),
                    []
                );

                const isSameConfig = removeDuplicateTabs?.every(
                    (config) => config?.cashback_redeem_percentage === firstIndexCashbackPercentage
                );

                defaultValue.is_same_config = isSameConfig;

                if (removeDuplicateTabs?.length) {
                    defaultValue.tabOrderingModes = removeDuplicateTabs;
                    setTabOrderingModes(removeDuplicateTabs);
                }

                const activeOrderingModes = defaultValue.ordering_mode_cashback_configurations.filter((config) =>
                    data.ordering_modes.some(
                        (orderingMode) => config.restaurant_ordering_mode_id === orderingMode.id && orderingMode.status
                    )
                );
                defaultValue.orderingModes = activeOrderingModes;

                const isSameConfigForEarnings = activeOrderingModes?.every(
                    (config) => config?.cashback_settlement_delay === firstIndexCashbackDelay
                );

                defaultValue.is_same_config_for_earnings_delay = isSameConfigForEarnings;

                setOrderingModes(activeOrderingModes);
            }
            const areAllSetToHundred = data.ordering_mode_cashback_configurations.every(
                (config) => config.cashback_redeem_percentage === 100
            );
            for (const orderingMode of data.ordering_mode_cashback_configurations) {
                if (orderingMode.cashback_redeem_percentage) {
                    defaultValue.advance_settings = true;
                }
            }

            if (defaultValue.advance_settings && !areAllSetToHundred && !data.settings.promo_consolidation) {
                setIsNavOpen(true);
            }

            if (defaultValue?.redeem_for_all_modes) {
                setCardDescription((prevState) => [
                    ...prevState,
                    `${defaultValue.redeem_for_all_modes} Redeem for ${
                        defaultValue.is_same_config ? ` all modes ` : `${firstOrderingMode?.[0]?.type}`
                    } | 100% Cashback Enable`,
                ]);
            }
            if (defaultValue.promo_consolidation) {
                setCardDescription((prevState) => [...prevState, 'Promo Consolidation On']);
            } else {
                setCardDescription((prevState) => [...prevState, 'Promo Consolidation Off']);
            }
        }

        return defaultValue;
    }, []);

    useEffect(() => {
        if (restaurantResponse?.restaurant) {
            const formValues = updateFormValues(restaurantResponse.restaurant);
            // Update the ordering modes state
            if (formValues.tabOrderingModes) {
                setTabOrderingModes(formValues.tabOrderingModes);
            }
            if (formValues.orderingModes) {
                setOrderingModes(formValues.orderingModes);
            }
            // Set card descriptions
            if (formValues.redeem_for_all_modes) {
                setCardDescription((prevState) => [
                    ...prevState,
                    `${formValues.redeem_for_all_modes} Redeem for ${formValues.is_same_config ? ` all modes ` : 'selected modes'} | 100% Cashback Enable`,
                ]);
            }
            if (formValues.promo_consolidation) {
                setCardDescription((prevState) => [...prevState, 'Promo Consolidation On']);
            } else {
                setCardDescription((prevState) => [...prevState, 'Promo Consolidation Off']);
            }
        }
    }, [restaurantResponse?.restaurant, updateFormValues]);

    const validatePercentage = useCallback((value) => {
        return value <= 100 || 'Percentage must be less than or quals to 100';
    }, []);

    return (
        <>
            <EuiForm component='form'>
                <>
                    <NumberField
                        label='Cashback Earning Percentage'
                        errors={errors}
                        append='%'
                        name='cashback_earning_percentage'
                        placeholder='Please Enter Cashback'
                        rules={{
                            required: 'Please Enter Cashback',
                            validate: validatePercentage,
                        }}
                        step='any'
                        control={control}
                    />

                    <NumberField
                        label='Cashback expiration duration'
                        errors={errors}
                        name='cashback_expiration_days'
                        placeholder='Please Enter duration'
                        rules={{
                            required: 'Please enter duration',
                        }}
                        // step='any'
                        control={control}
                        prepend={'In days'}
                    />
                    <NumberField
                        label='Cashback on signup'
                        errors={errors}
                        name='cashback_signup_bonus'
                        placeholder='Please Enter Cashback'
                        rules={{
                            required: 'Please Enter Cashback',
                        }}
                        append={'₹'}
                        step='any'
                        control={control}
                    />
                    <EuiSpacer />

                    <React.Fragment>
                        <EuiCard
                            layout='horizontal'
                            title={
                                <EuiFlexGroup
                                    direction='row'
                                    justifyContent='spaceBetween'
                                    alignItems='center'
                                    gutterSize='xs'
                                >
                                    <EuiFlexItem grow={10}>
                                        <EuiFlexGroup
                                            justifyContent='flexStart'
                                            alignItems='center'
                                            direction='row'
                                            gutterSize='s'
                                        >
                                            {props?.icon ? <EuiFlexItem grow={false}>{props.icon}</EuiFlexItem> : null}
                                            <EuiFlexItem grow={10}>
                                                <EuiFlexGroup
                                                    justifyContent='flexStart'
                                                    direction='column'
                                                    alignItems='flexStart'
                                                    gutterSize='xs'
                                                >
                                                    <EuiFlexItem>
                                                        <EuiFlexGroup
                                                            direction='row'
                                                            justifyContent='flexStart'
                                                            alignItems='center'
                                                            gutterSize='s'
                                                        >
                                                            <EuiFlexItem
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setIsNavOpen((prevState) => !prevState)}
                                                            >
                                                                <EuiTitle size='s'>
                                                                    <h2>Advance Settings</h2>
                                                                </EuiTitle>
                                                            </EuiFlexItem>
                                                            <EuiFlexItem grow={false}>
                                                                {!isNavOpen ? (
                                                                    <EuiLink
                                                                        style={{ fontSize: '12px' }}
                                                                        onClick={() => {
                                                                            setIsNavOpen((prevState) => !prevState);
                                                                        }}
                                                                    >
                                                                        <SwitchField
                                                                            name='advance_settings'
                                                                            control={control}
                                                                            label=''
                                                                            setValue={setValue}
                                                                        />
                                                                    </EuiLink>
                                                                ) : null}
                                                            </EuiFlexItem>
                                                        </EuiFlexGroup>
                                                    </EuiFlexItem>
                                                    <EuiFlexItem>
                                                        {cardDescription?.map((description, idx) => (
                                                            <EuiText
                                                                style={{ display: 'inline-flex' }}
                                                                size='xs'
                                                                key={idx}
                                                            >
                                                                {description}
                                                            </EuiText>
                                                        ))}
                                                    </EuiFlexItem>
                                                </EuiFlexGroup>
                                            </EuiFlexItem>
                                        </EuiFlexGroup>
                                    </EuiFlexItem>
                                    <EuiFlexItem>
                                        {isNavOpen ? (
                                            <EuiFlexItem
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setIsNavOpen((prevState) => !prevState);
                                                    setValue('advance_settings', false, { shouldDirty: true });
                                                }}
                                            >
                                                <EuiIcon type='cross' color='danger' />
                                            </EuiFlexItem>
                                        ) : null}
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            }
                        >
                            <EuiCollapsibleNavGroup
                                style={{ marginBottom: isNavOpen ? '0' : '-35px' }}
                                title={<></>}
                                titleSize='xs'
                                isCollapsible={true}
                                paddingSize='none'
                                initialIsOpen={isNavOpen}
                                forceState={isNavOpen ? 'open' : 'closed'}
                                arrowDisplay={'none'}
                            >
                                <CheckBoxField
                                    name='same_redemption_all_modes'
                                    control={control}
                                    label='Same Configurations for all modes'
                                    setValue={setValue}
                                />
                                <EuiSpacer />
                                {!watch('same_redemption_all_modes') ? (
                                    <>
                                        {/* {watch('ordering_mode_cashback_configurations')?.map( */}
                                        {tabOrderingModes?.map((orderingMode, index) => {
                                            return (
                                                <NumberField
                                                    labelStyles={{ textTransform: 'capitalize' }}
                                                    key={orderingMode.restaurant_ordering_mode_id.toString()}
                                                    label={`Redeem cashback for  ${
                                                        orderingMode.tab?.includes('_')
                                                            ? orderingMode.tab.replace('_', ' ')
                                                            : orderingMode.tab
                                                    }`}
                                                    errors={errors}
                                                    append='%'
                                                    // name={`ordering_mode_cashback_configurations[${index}].cashback_redeem_percentage`}
                                                    name={`tabOrderingModes[${index}].cashback_redeem_percentage`}
                                                    defaultValue={`${orderingMode?.cashback_redeem_percentage}`}
                                                    placeholder='Please Enter Cashback'
                                                    rules={{
                                                        required: 'Please Enter Cashback',
                                                        validate: validatePercentage,
                                                    }}
                                                    step='any'
                                                    control={control}
                                                />
                                            );
                                        })}
                                    </>
                                ) : (
                                    <NumberField
                                        label={'Redeem cashback'}
                                        errors={errors}
                                        name={`redeem_for_all_modes`}
                                        placeholder='Please Enter Cashback'
                                        rules={{
                                            required: 'Please Enter Cashback',
                                            validate: validatePercentage,
                                        }}
                                        append='%'
                                        step='any'
                                        control={control}
                                    />
                                )}
                                <ButtonGroup
                                    idSelected={watch('promo_consolidation') ? '1' : '0'}
                                    color='primary'
                                    control={control}
                                    setValue={setValue}
                                    name='promo_consolidation'
                                    watch={watch}
                                    label={'Consolidate other offers with cashback'}
                                />
                            </EuiCollapsibleNavGroup>
                        </EuiCard>
                        <EuiSpacer />
                        <EarningsDelay
                            watch={watch}
                            errors={errors}
                            control={control}
                            setValue={setValue}
                            orderingModes={orderingModes}
                        />
                    </React.Fragment>
                </>
            </EuiForm>
        </>
    );
};

export default React.memo(CashbackSettings);
